import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { resolveSplashBranding } from "../../../utils/splashBranding";
import { NAV_ICONS, IconSettings, IconBell } from "../../ui/icons";
import GlobalPatientSearch from "../GlobalPatientSearch";
import clinicAPI from "../../../services/clinicApi";
import clubSupportAPI from "../../../services/clubSupportApi";
import AdminSplash from "../AdminPortal/AdminSplash";
import NotificationsDropdown from "../common/NotificationsDropdown";
import {
  adminFirstName,
  adminGreeting,
  adminTodayLabel,
} from "../AdminPortal/adminAppUtils";
import {
  buildStaffSidebarNav,
  resolveStaffBottomTab,
  resolveStaffPageTitle,
  resolveStaffTabId,
} from "../../../utils/staffNav";
import { usePwaChrome, resolvePwaChromeConfig } from "../../../utils/pwaThemeColor";
import {
  usePullToRefresh,
  PullToRefreshIndicator,
} from "../../../hooks/usePullToRefresh";
import { dispatchAdminRefresh } from "../../../hooks/useAdminRefresh";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { usePortalHtmlClass } from "../../../hooks/usePortalHtmlClass";
import { useSkipPwaSplash, useShowPwaBanners, useStandalone } from "../../../hooks/useStandalone";
import {
  isPushSupported,
  registerServiceWorker,
  syncAdminPushSubscriptionIfGranted,
} from "../../../utils/pushNotifications";
import AdminPwaBanners from "../AdminPortal/AdminPwaBanners";
import SubscriptionGate from "../AdminPortal/SubscriptionGate";
import L from "../../../constants/baysportLabels";
import s from "../AdminPortal/adminApp.module.css";
import ManageDesktopShell from "../common/ManageDesktopShell";

export default function StaffAppShell({ children }) {
  const { pathname } = useLocation();
  const { tenant, profile, entitlements, billing } = useSelector((state) => state.auth);
  const skipSplash = useSkipPwaSplash();
  const showPwaBanners = useShowPwaBanners();
  const isMobile = useIsMobile(768);
  const standalone = useStandalone();
  const [splashDone, setSplashDone] = useState(skipSplash);
  const [chatBadge, setChatBadge] = useState(0);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyBadge, setNotifyBadge] = useState(0);
  const notifyBtnRef = useRef(null);
  const splashBranding = useMemo(
    () => resolveSplashBranding({ tenant, features: entitlements?.features }),
    [tenant, entitlements?.features],
  );
  const { theme, shellStyle, themeMode } = useClubPortalBranding(
    "aa",
    null,
    true,
    splashDone,
  );

  usePortalHtmlClass("admin");

  useEffect(() => {
    if (!splashDone || !isPushSupported()) return undefined;
    registerServiceWorker();
    syncAdminPushSubscriptionIfGranted();
    return undefined;
  }, [splashDone]);

  const handleSplashComplete = useCallback(() => setSplashDone(true), []);

  const loadChatBadge = useCallback(async () => {
    try {
      const [data, support] = await Promise.all([
        clinicAPI.getStaffChatInbox(),
        clubSupportAPI.getSupportUnread().catch(() => ({ unread: 0 })),
      ]);
      const chatTotal = (data.threads || []).reduce(
        (sum, t) => sum + (t.unread_count || 0),
        0,
      );
      setChatBadge(chatTotal + (Number(support.unread) || 0));
    } catch {
      setChatBadge(0);
    }
  }, []);

  const loadNotifyBadge = useCallback(async () => {
    try {
      const data = await clinicAPI.getMyAdminNotifications();
      setNotifyBadge(data.unread_count || 0);
    } catch {
      setNotifyBadge(0);
    }
  }, []);

  useEffect(() => {
    if (!splashDone) return undefined;
    loadChatBadge();
    loadNotifyBadge();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadChatBadge();
        loadNotifyBadge();
      }
    }, 15000);
    const onActivity = () => {
      loadChatBadge();
      loadNotifyBadge();
    };
    window.addEventListener("baysport:chat-activity", onActivity);
    window.addEventListener("baysport:support-activity", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:chat-activity", onActivity);
      window.removeEventListener("baysport:support-activity", onActivity);
    };
  }, [splashDone, loadChatBadge, loadNotifyBadge, pathname]);

  useEffect(() => {
    setNotifyOpen(false);
  }, [pathname]);

  const path = pathname.replace(/\/$/, "") || "/staff/app";
  const tab = resolveStaffTabId(pathname);
  const bottomTab = resolveStaffBottomTab(pathname);
  const isMoreHub = path === "/staff/app/more";
  const isOverview = tab === "overview";
  const isSchedule = tab === "schedule";
  const isChat = tab === "chat";
  const isJournal = tab === "journal";
  const isLife = tab === "life";
  const isProfile = tab === "profile";
  const pageEnterKey = tab === "chat" ? "staff-chat" : pathname;
  const hubTabs = new Set([
    "journal",
    "life",
    "notifications",
    "groups",
    "polls",
    "profile",
    "branding",
  ]);
  const hideTopBar =
    hubTabs.has(tab) || tab === "clients" || tab === "chat" || tab === "schedule";
  const selfHeader = isOverview || isMoreHub || tab === "life" || tab === "profile";
  const pageTitle =
    isLife || isProfile ? null : resolveStaffPageTitle(tab);
  const firstName = adminFirstName(profile?.full_name);

  const pwaChrome = resolvePwaChromeConfig({
    portal: "admin",
    themeMode,
    theme,
    immersive: isOverview,
  });

  usePwaChrome(splashDone && theme ? pwaChrome.color : "#141824", {
    enabled: isMobile || standalone,
    isDark: splashDone && theme ? pwaChrome.isDark : true,
    immersive: !splashDone || pwaChrome.immersive,
    themeColor: splashDone && theme ? pwaChrome.themeColor : "#141824",
    pageBg: pwaChrome.pageBg || "#f4f5f8",
  });

  const handlePullRefresh = useCallback(async () => {
    dispatchAdminRefresh();
    await new Promise((r) => setTimeout(r, 400));
  }, []);

  const noPtrTabs = new Set(["settings", "profile"]);
  const { pulling, pullDistance, threshold } = usePullToRefresh(handlePullRefresh, {
    enabled: splashDone && isMobile && !noPtrTabs.has(tab),
  });

  const useShellScroll = !isSchedule;
  const staffNav = buildStaffSidebarNav({ entitlements, tenant, billing });
  const desktopSections = [
    { items: staffNav.main },
    ...(staffNav.more?.length
      ? [{ label: "Разделы", items: staffNav.more }]
      : []),
  ];
  const flushDesktop =
    hubTabs.has(tab) || tab === "clients" || tab === "chat" || tab === "schedule";

  if (!isMobile && !standalone && splashDone) {
    return (
      <>
        <ManageDesktopShell
          homeTo="/staff/app"
          sections={desktopSections}
          profile={profile}
          profileTo="/staff/app/profile"
          chatBadge={chatBadge}
          notifyBadge={notifyBadge}
          notifyOpen={notifyOpen}
          onNotifyToggle={() => setNotifyOpen((v) => !v)}
          notifyBtnRef={notifyBtnRef}
          pageTitle={pageTitle}
          isOverview={isOverview}
          isSchedule={isSchedule}
          isChat={isChat}
          isJournal={isJournal}
          isLife={isLife}
          isProfile={isProfile}
          isSettings={false}
          flushContent={flushDesktop}
          shellStyle={shellStyle}
          brandingTheme={theme}
          showSearch={false}
        >
          <div key={pathname} className={s.pageEnter}>
            <SubscriptionGate>{children}</SubscriptionGate>
          </div>
        </ManageDesktopShell>

        <NotificationsDropdown
          open={notifyOpen}
          onClose={() => setNotifyOpen(false)}
          anchorRef={notifyBtnRef}
          allPath="/staff/app/notifications"
          portal="admin"
          onMarkedRead={loadNotifyBadge}
        />
      </>
    );
  }

  return (
    <>
      {!splashDone ? (
        <AdminSplash branding={splashBranding} onComplete={handleSplashComplete} />
      ) : null}

      <div
        className={`${s.shell} ${splashDone ? "" : s.shellBehindSplash}`}
        style={splashDone ? shellStyle : undefined}
      >
        {useShellScroll ? (
          <div className={s.shellScroll} data-pull-scroll>
            {isOverview ? (
              <div className={`${s.heroHome} ${splashDone ? "" : s.heroHidden}`}>
                <div
                  className={`${s.hero} ${
                    theme.coverUrl ? s.heroCover : s.heroGradient
                  }`}
                >
                  <div
                    className={s.heroBg}
                    key={theme.coverUrl || "solid"}
                    style={
                      theme.coverUrl
                        ? { backgroundImage: `url(${theme.coverUrl})` }
                        : { backgroundColor: theme.accent }
                    }
                    aria-hidden
                  />
                  <div className={s.heroTop}>
                    <div className={s.heroMeta}>
                      <p className={s.heroDate}>{adminTodayLabel()}</p>
                      <h1 className={s.heroTitle}>
                        {adminGreeting()}
                        {firstName ? `, ${firstName}` : ""}
                      </h1>
                      {tenant?.name ? (
                        <p className={s.heroClub}>{tenant.name}</p>
                      ) : null}
                    </div>
                    <div className={s.heroTopActions}>
                      <button
                        ref={notifyBtnRef}
                        type="button"
                        className={s.heroNotify}
                        title="Уведомления"
                        aria-label="Уведомления"
                        aria-expanded={notifyOpen}
                        onClick={() => setNotifyOpen((v) => !v)}
                      >
                        <IconBell width={20} height={20} />
                        {notifyBadge > 0 ? (
                          <span className={s.tabBadge}>
                            {notifyBadge > 9 ? "9+" : notifyBadge}
                          </span>
                        ) : null}
                      </button>
                      <Link
                        to="/staff/app/profile"
                        className={s.heroSettings}
                        title="Профиль"
                        aria-label="Профиль"
                      >
                        <IconSettings width={20} height={20} />
                      </Link>
                    </div>
                  </div>
                </div>
                <div className={s.searchFloat}>
                  <GlobalPatientSearch className={s.searchFullWidth} />
                </div>
              </div>
            ) : null}

            {!isOverview && !isMoreHub && !hideTopBar ? (
              <div className={`${s.topBar} ${splashDone ? "" : s.tabBarHidden}`}>
                {pageTitle ? (
                  <header className={s.pageHead}>
                    <h1 className={s.pageTitle}>{pageTitle}</h1>
                  </header>
                ) : null}
              </div>
            ) : null}

            <main className={selfHeader ? s.mainHome : s.main}>
              <SubscriptionGate profilePath="/staff/app/profile">
                <div key={pageEnterKey} className={s.pageEnter}>
                  {children}
                </div>
              </SubscriptionGate>
            </main>
          </div>
        ) : (
          <>
            {!isOverview && !isMoreHub && !hideTopBar ? (
              <div className={`${s.topBar} ${splashDone ? "" : s.tabBarHidden}`}>
                {pageTitle ? (
                  <header className={s.pageHead}>
                    <h1 className={s.pageTitle}>{pageTitle}</h1>
                  </header>
                ) : null}
              </div>
            ) : null}

            <main className={s.mainSchedule}>
              <SubscriptionGate profilePath="/staff/app/profile">
                <div key={pageEnterKey} className={s.pageEnter}>
                  {children}
                </div>
              </SubscriptionGate>
            </main>
          </>
        )}

        <PullToRefreshIndicator
          pulling={pulling}
          pullDistance={pullDistance}
          threshold={threshold}
        />

        {splashDone && showPwaBanners ? <AdminPwaBanners showPush={false} /> : null}

        <nav
          className={`${s.tabBar} ${splashDone ? s.tabBarReveal : s.tabBarHidden}`}
          aria-label="Навигация тренера"
        >
          {staffNav.bottomTabs.map((item) => {
            const Icon = NAV_ICONS[item.icon] || NAV_ICONS.overview;
            const active = item.id === bottomTab;
            const badge = item.id === "chat" && chatBadge > 0 ? chatBadge : 0;
            return (
              <Link
                key={item.id}
                to={item.to}
                className={active ? `${s.tab} ${s.tabActive}` : s.tab}
                aria-current={active ? "page" : undefined}
              >
                <span className={s.tabIconWrap}>
                  <Icon className={s.tabIcon} />
                  {badge > 0 ? (
                    <span className={s.tabBadge}>{badge > 9 ? "9+" : badge}</span>
                  ) : null}
                </span>
                <span className={s.tabLabel}>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <NotificationsDropdown
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        anchorRef={notifyBtnRef}
        allPath="/staff/app/notifications"
        portal="admin"
        onMarkedRead={loadNotifyBadge}
      />
    </>
  );
}
